import { assertNotEmpty } from '@festina-life/core'
import { useTranslate } from '@festina-life/ui/hooks'
import { Card, CardBody } from '@festina-life/ui/structure'
import cx from 'classnames'
import { useCallback, useMemo } from 'react'

import { useTemplateContext } from '../../context/templateContext'
import { useComparableField } from '../../context/useComparableField'
import { stringifyComparableValue } from '../diffIndicators/helpers'
import {
  type SerialiseValueParams,
  ValueSetupDiffIndicators,
} from '../diffIndicators/ValueSetupDiffIndicators'
import { InheritanceIndicator } from '../inheritanceIndicator/InheritanceIndicator'
import { TemplateDocumentationTooltip } from '../templateDocumentationTooltip/TemplateDocumentationTooltip'
import { TemplateFunctionDocumentation } from '../templateFieldDocumentation/TemplateFunctionDocumentation'
import {
  type ConfigurationTemplateFunctionValueWithPosition,
  functionTitle,
  mergeInheritedNullProps,
} from './fieldsHelpers'
import { TemplateFieldAnchor } from './TemplateFieldAnchor'

type Props = {
  data: ConfigurationTemplateFunctionValueWithPosition
}

export const TemplateFunctionNameCell = ({ data }: Props) => {
  const translate = useTranslate()
  const { templateVersion } = useTemplateContext()
  const functionName = useMemo(() => functionTitle(data), [data])

  assertNotEmpty(templateVersion?.versionInfo)

  const comparableField = useComparableField<typeof data>(data.path)

  const serialiseFunctionValue = useCallback(
    ({
      value: comparable,
      valueType,
    }: SerialiseValueParams<ConfigurationTemplateFunctionValueWithPosition>) => {
      if (!comparable?.function?.fplusSource && !comparable?.documentation) {
        return ''
      }

      const { position, isParentNotRelevant, ...comparableContent } = comparable

      let serialisableContent = comparableContent

      if (valueType === 'inherited') {
        serialisableContent = mergeInheritedNullProps({
          inheritedValue: comparable,
          currentValue: data as typeof comparable,
          keys: ['documentation'],
          currentValuePath: data.path,
        })
      }

      return stringifyComparableValue(serialisableContent)
    },
    [data],
  )

  return (
    <div className="relative flex size-full flex-row">
      <TemplateFieldAnchor path={data.path} />
      <div className="grid size-full grid-cols-[minmax(0,1fr)_40px] items-center gap-x-2">
        <TemplateDocumentationTooltip
          tip={
            <Card>
              <CardBody
                outerClassName="min-w-250"
                innerClassName="grid grid-cols-[auto_1fr] items-start gap-x-10 gap-y-6"
              >
                <TemplateFunctionDocumentation
                  areFieldsShownAsEditable={false}
                  data={data}
                />
              </CardBody>
            </Card>
          }
          className="h-full min-w-0"
        >
          <div className="relative flex size-full min-w-0 items-center justify-between gap-x-2 pl-4">
            <div className="min-w-0">
              <div
                className={cx(
                  'truncate',
                  data.isParentNotRelevant ? 'line-through' : undefined,
                )}
              >
                {functionName}
              </div>
              <div className="truncate text-sm opacity-60">
                {data.type === 'declared'
                  ? translate('templateViewer.functionType.declared')
                  : translate('templateViewer.functionType.dynamic')}
              </div>
            </div>
            <InheritanceIndicator value={data} className="shrink-0" />
          </div>
        </TemplateDocumentationTooltip>
        <div className="w-10">
          <ValueSetupDiffIndicators
            comparableValues={comparableField}
            serialiseValueFn={serialiseFunctionValue}
          />
        </div>
      </div>
    </div>
  )
}
